/**
 * Canonical text input primitive — composes design tokens, brand CSS
 * vars, and the existing `getInputStyles(isDarkMode)` helper so dark
 * mode keeps working without duplicating color logic.
 *
 *   <Input label="Email" type="email" required />
 *   <Input size="sm" placeholder="Search…" />
 *   <Input label="Phone" error="Required field" />
 *   <Input label="Notes" hint="Visible to staff only" />
 *   <Input wrapperClassName="sm:col-span-3" />   // classes for the WRAPPER
 *   <Input fullWidth={false} wrapperClassName="w-32" />  // narrower wrapper
 *
 * For non-input form controls (textarea, select), use the matching
 * `<Textarea>` and `<Select>` primitives — they share the same API.
 */

import React, { useId } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import { TOKENS, type InputSize } from "../../utils/designTokens";
import Label from "./Label";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Rendered as a `<Label>` above the input. Accepts JSX so labels can include `<strong>`, code spans, etc. */
  label?: React.ReactNode;
  /** Padding + font-size bucket. Defaults to `md`. */
  size?: InputSize;
  /** Validation error — renders red text below the input + red ring. */
  error?: string;
  /** Helper text — renders muted below the input (suppressed if error is set). */
  hint?: string;
  /** Visual required marker on the label. (Pass `required` for the form-level enforcement.) */
  requiredMarker?: boolean;
  /**
   * Classes for the WRAPPER div, not the control. `className` reaches the
   * control only, so a grid span or a width constraint had no route in — two
   * separate sweeps wrapped the primitive in an extra div to get one.
   */
  wrapperClassName?: string;
  /**
   * Emit `w-full` on the wrapper. Defaults to true.
   *
   * Must be a prop rather than something `wrapperClassName` overrides, for the
   * same reason Card's `background` is: Tailwind resolves same-specificity
   * utilities by stylesheet SOURCE order, and `.w-full` is emitted AFTER the
   * numeric widths (measured in the built CSS: w-32 @33635, w-64 @33813,
   * w-full @34175). So a caller passing `w-32` would still render full width.
   */
  fullWidth?: boolean;
  /**
   * Icon rendered inside the control at its leading edge; the control gains
   * pl-10 to make room.
   *
   * This has to live in the primitive. The icon is absolutely positioned
   * against a relative box wrapping the control, and `className` reaches the
   * control itself — a caller has no way to introduce that box. 56 such
   * adornments across 39 files are hand-rolled for exactly this reason, and
   * each one keeps its page off the primitive.
   *
   * pl-10 does beat the size bucket's px-*: .pl-10 is emitted at byte 82561 of
   * the built stylesheet, after .px-3 (81369) and .px-4 (81416). Measured, not
   * assumed — if it lost, the icon would sit on top of the text.
   */
  leadingIcon?: React.ReactNode;
}

// forwardRef so a caller can focus the control (a search box that takes focus
// on open). Without it, those inputs stayed hand-rolled (10-31 sweep).
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  size = "md",
  error,
  hint,
  requiredMarker,
  required,
  id: providedId,
  className = "",
  wrapperClassName = "",
  fullWidth = true,
  leadingIcon,
  ...rest
}, ref) => {
  const { isDarkMode } = useTheme();
  const generatedId = useId();
  const id = providedId || generatedId;

  const inputClasses = [
    TOKENS.input.base,
    TOKENS.input.sizes[size],
    getInputStyles(isDarkMode),
    error ? "border-red-500 focus:ring-red-500" : "",
    leadingIcon ? "pl-10" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [fullWidth ? "w-full" : "", wrapperClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses}>
      {label && (
        <Label htmlFor={id} required={requiredMarker || required}>
          {label}
        </Label>
      )}
      {/* The positioning box is rendered ONLY when there is an icon.
          Wrapping unconditionally would insert a div into the DOM of every
          Input and Select in the app — which broke a test asserting on the
          wrapper via parentElement, and would silently have broken any
          caller-side CSS relying on the existing structure.

          aria-hidden + pointer-events-none: the icon is decoration, and a
          click on it must reach the control underneath. */}
      {leadingIcon ? (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-gray-400 dark:text-gray-500"
          >
            {leadingIcon}
          </span>
        <input
          ref={ref}
          id={id}
          required={required}
          className={inputClasses}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          {...rest}
        />
        </div>
      ) : (
      <input
        ref={ref}
        id={id}
        required={required}
        className={inputClasses}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
      )}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
});

Input.displayName = "Input";

export default Input;
